// frontend/src/components/WaypointFormCard.jsx
// Reusable waypoint/stop form card for route management (add and edit modes)

import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import MultiLanguageField from './common/MultiLanguageField';
import { getImageUrl } from '../utils/imageUrl';

const WaypointFormCard = ({
  waypoint,
  index,
  isFirst,
  isLast,
  totalCount,
  mode = 'add',
  advancedMode = false,
  restrictedEditing = false,

  // Callbacks
  onStopChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  onPickLocation,
  onAddressBlur,

  // Picking state
  pickingMode,
  currentStopIndex,
  onCancelPicking,

  // Image upload
  boardingImagePreview,
  boardingImage,
  onImageSelect,
  onImageUpload,
  onImageDelete,
  onClearImageSelection,
  uploadingImage = false,

  // Entity context
  entityId,
  entitySettings,

  // For determining if stop can be removed (edit mode)
  canRemoveStop = true,
}) => {
  const { t } = useTranslation();
  const { background, text, border, components, cn } = useTheme();

  // Disabled style for buttons
  const disabledStyle = 'disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300';

  // Determine the label for this waypoint
  const getWaypointLabel = () => {
    if (isFirst) return t('startLocation');
    if (isLast) return t('endLocation');
    return `${t('stop')} ${index}`;
  };

  // Check if this is a stop type
  const isStop = waypoint.point_type === 'stop';
  const isStopOrWaypoint = waypoint.point_type === 'stop' || waypoint.point_type === 'waypoint';

  // Get image source
  const getImageSrc = () => {
    if (boardingImagePreview) return boardingImagePreview;
    if (waypoint.boarding_location_image) {
      return getImageUrl(`/uploads/stops/${waypoint.boarding_location_image}`);
    }
    return null;
  };

  const imageSrc = getImageSrc();
  const imageInputId = mode === 'add'
    ? `boarding-image-add-${index}`
    : `boarding-image-edit-${waypoint.id}-${index}`;

  return (
    <ThemedCard
      className="mb-4"
      id={mode === 'add' ? `stop-${index}` : `waypoint-${index}`}
    >
      <ThemedCard.Body>
        {/* Header with label and move buttons */}
        <div className="flex justify-between items-center mb-2">
          <span className={cn("font-medium", text.primary)}>
            {getWaypointLabel()}
          </span>
          <div className="flex space-x-2">
            <button
              type="button"
              className={cn(
                "p-1 rounded transition-colors",
                isFirst || restrictedEditing
                  ? cn("cursor-not-allowed", text.disabled)
                  : cn("hover:bg-blue-100 dark:hover:bg-blue-900/30", text.link),
                disabledStyle
              )}
              onClick={() => onMoveUp(index)}
              disabled={isFirst || restrictedEditing}
              title={t('moveUp')}
            >
              <ChevronUp size={20} />
            </button>
            <button
              type="button"
              className={cn(
                "p-1 rounded transition-colors",
                isLast || restrictedEditing
                  ? cn("cursor-not-allowed", text.disabled)
                  : cn("hover:bg-blue-100 dark:hover:bg-blue-900/30", text.link),
                disabledStyle
              )}
              onClick={() => onMoveDown(index)}
              disabled={isLast || restrictedEditing}
              title={t('moveDown')}
            >
              <ChevronDown size={20} />
            </button>
          </div>
        </div>

        {/* Name field */}
        <div className="mb-2">
          <MultiLanguageField
            entityId={entityId}
            entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
            multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
            contentType="waypoint"
            contentId={mode === 'edit' ? waypoint.id : null}
            fieldName="name"
            defaultValue={waypoint.name}
            onChange={(fieldName, value, isDefault) => {
              if (isDefault) {
                onStopChange({ target: { value } }, index, 'name', value);
              }
            }}
            label={t('stopName')}
            required={true}
            multiline={false}
            disabled={false}
            placeholder={
              isFirst ? t('enterStartLocationName') :
              isLast ? t('enterEndLocationName') :
              t('enterStopName')
            }
            onFocus={(e) => e.target?.select()}
          />
        </div>

        {/* Address field */}
        <div className="mb-2">
          <MultiLanguageField
            entityId={entityId}
            entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
            multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
            contentType="waypoint"
            contentId={mode === 'edit' ? waypoint.id : null}
            fieldName="address"
            defaultValue={waypoint.address}
            onChange={(fieldName, value, isDefault) => {
              if (isDefault) {
                onStopChange({ target: { value } }, index, 'address', value);
              }
            }}
            label={t('address')}
            required={false}
            multiline={false}
            disabled={restrictedEditing}
            placeholder={t('address')}
            onBlur={(e) => onAddressBlur(e, index)}
          />
        </div>

        {/* Coordinates display */}
        <p className={cn("text-sm mb-2", text.muted)}>
          {t('coordinates')}: {' '}
          {waypoint.latitude && waypoint.longitude ? (
            <>
              {t('Lat')}: {Number(waypoint.latitude).toFixed(6)},
              {t('Lng')}: {Number(waypoint.longitude).toFixed(6)}
            </>
          ) : (
            'N/A'
          )}
        </p>

        {/* Pick Location Button */}
        <div className="flex space-x-2 mb-4">
          <ThemedButton
            type="button"
            variant="secondary"
            onClick={() => onPickLocation(index)}
            disabled={restrictedEditing}
          >
            {t('pickLocation')}
          </ThemedButton>
          {pickingMode === 'stop' && currentStopIndex === index && (
            <ThemedButton
              type="button"
              variant="danger"
              onClick={onCancelPicking}
            >
              {t('cancel')}
            </ThemedButton>
          )}
        </div>

        {/* Advanced Settings */}
        {!restrictedEditing && (
          <div className={cn("mt-4 p-4 rounded", background.secondary)}>
            <h4 className={cn("font-medium mb-2", text.primary)}>{t('advancedSettings')}</h4>

            {/* Point type selector */}
            <select
              className={cn(
                "w-full p-2 mb-2 border rounded transition-all duration-200",
                components.input.base,
                components.input.focus
              )}
              value={waypoint.point_type || 'stop'}
              onChange={(e) => onStopChange(e, index, 'point_type')}
            >
              <option value="stop">{t('regularStop')}</option>
              {advancedMode && (
                <option value="waypoint">{t('waypoint')}</option>
              )}
            </select>

            {/* is_required and can_reorder - for stops and waypoints */}
            {isStopOrWaypoint && (
              <div className="flex space-x-4 mb-2">
                <label className="flex items-center" title={t('alwaysVisitHelp')}>
                  <input
                    type="checkbox"
                    checked={waypoint.is_required !== false}
                    disabled={isFirst || isLast}
                    onChange={(e) => onStopChange(e, index, 'is_required', e.target.checked)}
                    className="mr-2"
                  />
                  {t('alwaysVisit')}
                </label>
                <label className="flex items-center" title={t('canReorderHelp')}>
                  <input
                    type="checkbox"
                    checked={waypoint.can_reorder === true}
                    disabled={isFirst || isLast}
                    onChange={(e) => onStopChange(e, index, 'can_reorder', e.target.checked)}
                    className="mr-2"
                  />
                  {t('canReorder')}
                </label>
              </div>
            )}

            {/* Stop-specific fields: pickup/dropoff */}
            {isStop && (
              <>
                <div className="flex space-x-4 mb-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={waypoint.can_pickup}
                      disabled={isLast}
                      onChange={(e) => onStopChange(e, index, 'can_pickup', e.target.checked)}
                      className="mr-2"
                    />
                    {t('allowPickup')}
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={waypoint.can_dropoff}
                      disabled={isFirst}
                      onChange={(e) => onStopChange(e, index, 'can_dropoff', e.target.checked)}
                      className="mr-2"
                    />
                    {t('allowDropoff')}
                  </label>
                </div>

                {/* Stay duration */}
                <div className="flex space-x-2">
                  <div className="w-1/2 relative group">
                    <ThemedInput
                      type="number"
                      placeholder={t('stayDurationMin')}
                      value={waypoint.stay_duration_minutes || ''}
                      onChange={(e) => onStopChange(e, index, 'stay_duration_minutes')}
                      title={t('Time needed for boarding/unloading at this stop')}
                    />
                    <div className={cn(
                      "hidden group-hover:block absolute z-50 w-48 p-2 mt-1 text-sm rounded-lg",
                      "text-white bg-gray-800 dark:bg-gray-700"
                    )}>
                      {t('Time needed for boarding/unloading at this stop')}
                    </div>
                    <span className={cn("text-xs mt-1", text.muted)}>
                      ⏱️ {t('stayDurationMin')}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Boarding Location Information - Only for stops */}
        {isStop && (
          <div className={cn("mt-4 p-4 rounded", background.secondary)}>
            <h4 className={cn("font-medium mb-2", text.primary)}>{t('boardingInfo')}</h4>

            {/* Boarding location info text */}
            <div className="mb-2">
              <MultiLanguageField
                entityId={entityId}
                entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
                multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
                contentType="waypoint"
                contentId={mode === 'edit' ? waypoint.id : null}
                fieldName="boarding_location_info"
                defaultValue={waypoint.boarding_location_info || ''}
                onChange={(fieldName, value, isDefault) => {
                  if (isDefault) {
                    onStopChange({ target: { value } }, index, 'boarding_location_info', value);
                  }
                }}
                label={t('boardingLocationInfo')}
                required={false}
                multiline={true}
                disabled={restrictedEditing}
                placeholder={t('enterBoardingLocationInfo')}
              />
            </div>

            {/* Boarding location URL */}
            <div className="mb-2">
              <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                {t('boardingLocationUrl')}
              </label>
              <ThemedInput
                type="url"
                value={waypoint.boarding_location_url || ''}
                onChange={(e) => onStopChange(e, index, 'boarding_location_url')}
                disabled={restrictedEditing}
                placeholder={t('boardingLocationUrlPlaceholder')}
              />
              <p className={cn("text-xs mt-1", text.muted)}>
                {t('boardingLocationUrlHint')}
              </p>
            </div>

            {/* Boarding Location Image Upload */}
            <div className="mb-2">
              <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                {t('boardingLocationImage')}
              </label>

              {/* Show existing or preview image */}
              {imageSrc && (
                <div className="mb-2">
                  <div className="relative inline-block">
                    <img
                      src={imageSrc}
                      alt={t('boardingLocationImage')}
                      className={cn("h-32 w-auto object-contain border rounded", border.default)}
                    />
                    {/* Clear selection button for new uploads */}
                    {boardingImage && (
                      <button
                        type="button"
                        onClick={() => onClearImageSelection(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        disabled={restrictedEditing}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    {/* Delete button for existing images (edit mode only) */}
                    {waypoint.boarding_location_image && !boardingImage && !restrictedEditing && (
                      <button
                        type="button"
                        onClick={() => onImageDelete(index, waypoint.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        title={t('deleteImage')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id={imageInputId}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => onImageSelect(index, e)}
                  className="hidden"
                  disabled={restrictedEditing}
                />
                <label
                  htmlFor={imageInputId}
                  className={cn(
                    "inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors",
                    restrictedEditing
                      ? "cursor-not-allowed bg-gray-100 text-gray-400"
                      : cn("cursor-pointer", components.button.secondary)
                  )}
                >
                  {t('selectImage')}
                </label>

                {/* Upload button - only show for existing waypoints with selected image */}
                {waypoint.id && boardingImage && (
                  <ThemedButton
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => onImageUpload(index, waypoint.id)}
                    disabled={uploadingImage || restrictedEditing}
                  >
                    {uploadingImage ? t('uploading') : t('uploadImage')}
                  </ThemedButton>
                )}
              </div>

              <p className={cn("text-xs mt-1", text.muted)}>
                {t('boardingImageHelp')}
              </p>
            </div>
          </div>
        )}

        {/* Remove Stop Button */}
        {canRemoveStop && totalCount > 2 && (
          <div className="flex space-x-2 mt-4">
            <ThemedButton
              type="button"
              variant="danger"
              onClick={() => onRemove(index)}
              disabled={restrictedEditing}
            >
              {t('removeStop')}
            </ThemedButton>
          </div>
        )}
      </ThemedCard.Body>
    </ThemedCard>
  );
};

export default WaypointFormCard;
